import React, { useEffect } from "react"
import { HConfig } from "src/core/helpers"
import AppConfig from "src/App/AppConfig"
import { HiOutlineViewGrid } from "react-icons/hi"
import {
  MdOutlineSpaceDashboard,
  MdOutlineAddCircleOutline,
} from "react-icons/md"
import { BiSearchAlt } from "react-icons/bi"
import { BsFolder2Open } from "react-icons/bs"

const css = `
@keyframes homeShimmer {
  0%   { background-position: -400px 0; }
  100% { background-position: 400px 0; }
}
@keyframes homeFloat {
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-10px); }
}
@keyframes homeFadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
}
@keyframes homeOrbit {
  from { transform: rotate(0deg) translateX(110px) rotate(0deg); }
  to   { transform: rotate(360deg) translateX(110px) rotate(-360deg); }
}
@keyframes homeOrbit2 {
  from { transform: rotate(120deg) translateX(80px) rotate(-120deg); }
  to   { transform: rotate(480deg) translateX(80px) rotate(-480deg); }
}
@keyframes homeOrbit3 {
  from { transform: rotate(240deg) translateX(140px) rotate(-240deg); }
  to   { transform: rotate(600deg) translateX(140px) rotate(-600deg); }
}
@keyframes homeIconFloat1 {
  0%   { transform: translate(0px, 0px) rotate(0deg); }
  25%  { transform: translate(12px, -18px) rotate(8deg); }
  50%  { transform: translate(-8px, -30px) rotate(-5deg); }
  75%  { transform: translate(-16px, -12px) rotate(10deg); }
  100% { transform: translate(0px, 0px) rotate(0deg); }
}
@keyframes homeIconFloat2 {
  0%   { transform: translate(0px, 0px) rotate(0deg); }
  25%  { transform: translate(-14px, 16px) rotate(-10deg); }
  50%  { transform: translate(10px, 28px) rotate(6deg); }
  75%  { transform: translate(18px, 10px) rotate(-8deg); }
  100% { transform: translate(0px, 0px) rotate(0deg); }
}
@keyframes homeIconFloat3 {
  0%   { transform: translate(0px, 0px) rotate(0deg); }
  33%  { transform: translate(20px, 14px) rotate(12deg); }
  66%  { transform: translate(-10px, 22px) rotate(-6deg); }
  100% { transform: translate(0px, 0px) rotate(0deg); }
}
@keyframes homeRingPulse {
  0%, 100% { transform: translate(-50%, -50%) scale(1); }
  50%       { transform: translate(-50%, -50%) scale(1.1); }
}

.home-wrap {
  min-height: 72vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #022b66 0%, #043d91 50%, #03337b 100%);
  border-radius: 16px;
  overflow: hidden;
  position: relative;
}

/* floating icons */
.home-icon {
  position: absolute;
  color: rgba(255,255,255,0.12);
  pointer-events: none;
  z-index: 1;
}
.home-icon svg { display: block; }

.home-blob1, .home-blob2 {
  position: absolute;
  border-radius: 50%;
  filter: blur(80px);
  pointer-events: none;
}
.home-blob1 {
  width: 420px; height: 420px;
  background: radial-gradient(circle, rgba(255,255,255,0.18) 0%, transparent 70%);
  top: -100px; left: -100px;
}
.home-blob2 {
  width: 360px; height: 360px;
  background: radial-gradient(circle, rgba(255,255,255,0.10) 0%, transparent 70%);
  bottom: -80px; right: -60px;
}

/* pulsing ring */
.home-ring {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 340px; height: 340px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.18);
  animation: homeRingPulse 4s ease-in-out infinite;
}
.home-ring2 {
  position: absolute;
  top: 50%; left: 50%;
  transform: translate(-50%, -50%);
  width: 480px; height: 480px;
  border-radius: 50%;
  border: 1px solid rgba(255,255,255,0.09);
  animation: homeRingPulse 4s ease-in-out 1s infinite;
}

/* orbiting dots */
.home-dot {
  position: absolute;
  top: 50%; left: 50%;
  width: 8px; height: 8px;
  margin: -4px;
  border-radius: 50%;
}
.home-dot1 {
  background: #6088b3;
  animation: homeOrbit 7s linear infinite;
  box-shadow: 0 0 8px 2px rgba(96,136,179,0.6);
}
.home-dot2 {
  background: #98c1d9;
  width: 6px; height: 6px; margin: -3px;
  animation: homeOrbit2 5s linear infinite;
  box-shadow: 0 0 6px 2px rgba(152,193,217,0.5);
}
.home-dot3 {
  background: #4a7fa8;
  width: 5px; height: 5px; margin: -2.5px;
  animation: homeOrbit3 9s linear infinite;
  box-shadow: 0 0 5px 1px rgba(74,127,168,0.5);
}

/* content */
.home-content {
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 20px;
  animation: homeFadeIn 0.7s ease both;
}

.home-label {
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.75);
  margin-bottom: 20px;
  animation: homeFadeIn 0.6s ease 0.1s both;
}

.home-title {
  font-size: clamp(30px, 5vw, 52px);
  font-weight: 700;
  color: #f1f5f9;
  margin: 0 0 8px 0;
  line-height: 1.15;
}

.home-title span {
  display: inline-block;
  background: linear-gradient(90deg, #ffd766, #ffb347);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
}

.home-sub {
  font-size: 18px;
  color: rgba(255,255,255,0.55);
  letter-spacing: 1px;
  margin-top: 24px;
}
`

export default function Home() {
  useEffect(() => {
    HConfig.changeTitle("Home", "Home")
    return () => {
      HConfig.changeTitle(null, "")
    }
  }, [])

  const title = AppConfig.appInfo.title
  const userName = HConfig.getAuthUserName()

  return (
    <>
      <style>{css}</style>
      <div className="home-wrap">
        {/* floating icons */}
        <span
          className="home-icon"
          style={{
            top: "12%",
            left: "8%",
            fontSize: 48,
            animation: "homeIconFloat1 8s ease-in-out infinite",
          }}
        >
          <HiOutlineViewGrid />
        </span>
        <span
          className="home-icon"
          style={{
            top: "70%",
            left: "6%",
            fontSize: 36,
            animation: "homeIconFloat2 10s ease-in-out infinite",
          }}
        >
          <MdOutlineSpaceDashboard />
        </span>
        <span
          className="home-icon"
          style={{
            top: "15%",
            right: "9%",
            fontSize: 42,
            animation: "homeIconFloat3 9s ease-in-out infinite",
          }}
        >
          <BiSearchAlt />
        </span>
        <span
          className="home-icon"
          style={{
            top: "65%",
            right: "7%",
            fontSize: 40,
            animation: "homeIconFloat1 11s ease-in-out 1s infinite",
          }}
        >
          <BsFolder2Open />
        </span>
        <span
          className="home-icon"
          style={{
            bottom: "10%",
            left: "45%",
            fontSize: 32,
            animation: "homeIconFloat2 7s ease-in-out 0.5s infinite",
          }}
        >
          <MdOutlineAddCircleOutline />
        </span>

        <div className="home-blob1" />
        <div className="home-blob2" />
        <div className="home-ring" />
        <div className="home-ring2" />
        <div className="home-dot home-dot1" />
        <div className="home-dot home-dot2" />
        <div className="home-dot home-dot3" />

        <div className="home-content">
          <h1 className="home-title">
            Welcome <span>{userName}</span> to {title}
          </h1>
          <div className="home-sub">Have a productive day!</div>
        </div>
      </div>
    </>
  )
}
